<template>
  <div>
    <div class="card shadow-sm" v-if="caisse">
      <div class="card-header border-bottom">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h4 class="text-dark">
              <a-button @click="$router.back()" type="link" class="p-0">
                <a-icon type="arrow-left" />
              </a-button>
              {{ caisse.name }}
              <small class="text-muted">({{ caisse.reference }})</small>
              <a-tag
                :color="caisse.availability === 'OPEN' ? 'green' : 'red'"
                class="ml-2"
              >
                {{
                  caisse.availability === "OPEN"
                    ? $t("caisse.ouverte").toUpperCase()
                    : $t("caisse.fermee").toUpperCase()
                }}
              </a-tag>
            </h4>

            <div class="d-flex align-items-center text-secondary">
              <a-icon type="user" class="mr-2" />
              <span class="font-weight-medium mr-2"
                >{{
                  ownerNames.length > 1
                    ? $t("caisse.proprietaires")
                    : $t("caisse.proprietaire")
                }}:</span
              >
              <span class="text-dark">
                {{ ownerNames.length ? ownerNames.join(", ") : "N/A" }}
              </span>
            </div>
          </div>
          <div class="mt-2 float-right">
            <a-spin :spinning="loading">
              <a-statistic
                :title="$t('caisse.balance')"
                :value="formatCurrency(caisse.balance)"
                :precision="2"
                class="mb-0"
                value-style="font-size: 1.1rem;"
              />
            </a-spin>
          </div>
        </div>
      </div>

      <div class="card-body">
        <a-tabs v-model="activeTab" class="custom-tabs" tab-position="left">
          <a-tab-pane key="general" :tab="$t('caisse.general')">
            <CaisseGeneralTab :key="tabKeys.general" :caisseId="caisse._id" />
          </a-tab-pane>
          <a-tab-pane key="edit" :tab="$t('caisse.modifier')">
            <CaisseEditTab
              :key="tabKeys.edit"
              :caisse="caisse"
              @updated="fetchCaisse"
            />
          </a-tab-pane>
          <a-tab-pane key="access" :tab="$t('caisse.acces')">
            <CaisseAccessTab
              :key="tabKeys.access"
              :caisseId="caisse._id"
              :authorizedUsers="caisse.authorizedUsers"
              @updated="fetchCaisse"
            />
          </a-tab-pane>
          <a-tab-pane
            key="transfer"
            :tab="$t('caisse.transfers')"
            v-if="caisse.type === 'PRINCIPAL'"
          >
            <CaisseTransferTab
              :key="tabKeys.transfer"
              :caisseId="caisse._id"
              :caisseType="caisse.type"
              @success="handleTransferSuccess"
            />
          </a-tab-pane>
          <a-tab-pane key="history" :tab="$t('caisse.historique')">
            <CaisseHistoryTab
              :key="tabKeys.history"
              ref="historyTab"
              :caisseId="caisse._id"
            />
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>
    <div
      class="d-flex justify-content-center align-items-center min-vh-25"
      v-else
    >
      <a-spin />
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import CaisseTransferTab from "./components/CaisseTransferTab.vue";
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  components: {
    CaisseEditTab: () => import("./components/CaisseEditTab.vue"),
    CaisseAccessTab: () => import("./components/CaisseAccessTab.vue"),
    CaisseHistoryTab: () => import("./components/CaisseHistoryTab.vue"),
    CaisseGeneralTab: () => import("./components/CaisseGeneralTab.vue"),
    CaisseTransferTab,
  },
  props: ["id"],
  data() {
    return {
      caisse: null,
      activeTab: "general",
      loading: false,
      currency: null,
      tabKeys: {
        general: Date.now() + "-general",
        edit: Date.now() + "-edit",
        access: Date.now() + "-access",
        transfer: Date.now() + "-transfer",
        history: Date.now() + "-history",
      },
    };
  },
  computed: {
    ownerNames() {
      return ((this.caisse && this.caisse.authorizedUsers) || [])
        .filter((u) => u.permissions.includes("OWNER"))
        .map((u) => u.userName);
    },
  },
  created() {
    this.currency = getBuildingCurrency();
    this.fetchCaisse();
  },
  watch: {
    activeTab(newTab) {
      // Update the key for the activated tab to force remount
      this.$set(this.tabKeys, newTab, Date.now() + "-" + newTab);
    },
  },
  methods: {
    formatCurrency(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },
    handleTransferSuccess() {
      this.fetchCaisse();
      this.$refs.historyTab.fetchTransfers();
    },
    async fetchCaisse() {
      try {
        this.loading = true;
        const res = await apiClient.get(`/compV3/caisse/${this.id}`);
        this.caisse = res.data;
      } catch (e) {
        console.log(e);
        this.$message.error(this.$t("caisse.erreurChargementCaisse"));
        this.$router.replace("/manageCaisses");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.custom-tabs :deep(.ant-tabs-nav) {
  margin-bottom: 1.5rem;
}

.custom-tabs :deep(.ant-tabs-tab) {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
}

.custom-tabs :deep(.ant-tabs-tab-active) {
  font-weight: 500;
}

@media (max-width: 768px) {
  .card-header .d-flex {
    flex-direction: column;
  }

  .card-header .d-flex > div:last-child {
    margin-top: 1rem;
  }
}
</style>
